/**
 * Shared calculations and wealth tier configurations for My Wealth
 */

export interface FinancialAssets {
  cashAndSavings: number;
  investments: number;
  retirementAccounts: number;
  propertyValue: number;
  vehicles: number;
  otherAssets: number;
}

export interface FinancialLiabilities {
  creditCardDebt: number;
  studentLoans: number;
  homeLoans: number;
  otherDebts: number;
}

export interface FinancialCashFlow {
  monthlyIncome: number;
  monthlyExpenses: number;
}

export type WealthTierKey = 'empty' | 'negative' | 'starter' | 'building' | 'comfortable' | 'established' | 'premium';

export interface CurrencyOption {
  code: string;
  symbol: string;
  name: string;
}

export const ALL_CURRENCIES: CurrencyOption[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar (USD)' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee (INR)' },
  { code: 'EUR', symbol: '€', name: 'Euro (EUR)' },
  { code: 'GBP', symbol: '£', name: 'British Pound (GBP)' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar (CAD)' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar (AUD)' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen (JPY)' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham (AED)' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar (SGD)' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc (CHF)' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan (CNY)' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar (NZD)' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar (HKD)' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona (SEK)' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone (NOK)' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone (DKK)' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won (KRW)' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real (BRL)' },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso (MXN)' },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal (SAR)' },
  { code: 'QAR', symbol: 'QAR', name: 'Qatari Riyal (QAR)' },
  { code: 'KWD', symbol: 'KWD', name: 'Kuwaiti Dinar (KWD)' },
  { code: 'BHD', symbol: 'BHD', name: 'Bahraini Dinar (BHD)' },
  { code: 'OMR', symbol: 'OMR', name: 'Omani Rial (OMR)' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand (ZAR)' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht (THB)' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit (MYR)' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah (IDR)' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso (PHP)' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong (VND)' },
  { code: 'PKR', symbol: 'PKR', name: 'Pakistani Rupee (PKR)' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka (BDT)' },
  { code: 'LKR', symbol: 'LKR', name: 'Sri Lankan Rupee (LKR)' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira (NGN)' },
  { code: 'EGP', symbol: 'EGP', name: 'Egyptian Pound (EGP)' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira (TRY)' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty (PLN)' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna (CZK)' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint (HUF)' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel (ILS)' },
  { code: 'CLP', symbol: 'CLP$', name: 'Chilean Peso (CLP)' },
  { code: 'COP', symbol: 'COL$', name: 'Colombian Peso (COP)' },
  { code: 'ARS', symbol: 'ARS$', name: 'Argentine Peso (ARS)' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar (TWD)' }
];

export interface WealthTierInfo {
  key: WealthTierKey;
  name: string;
  badgeLabel: string;
  description: string;
  minNetWorth: number;
  maxNetWorth: number;
  accentColor: string;
  roomTheme: {
    roomLayout: 'starter' | 'expanded' | 'suite' | 'estate' | 'penthouse';
    wallColor: string;
    floorColor: string;
    rugColor: string;
    lightingMood: string;
    maxDecorations: number;
  };
}

export const WEALTH_TIER_CONFIG: Record<WealthTierKey, WealthTierInfo> = {
  empty: {
    key: 'empty',
    name: 'Empty Plot',
    badgeLabel: 'Ground Zero',
    description: 'No wealth data entered. Enter your assets, liabilities, or income below to construct your house.',
    minNetWorth: 0,
    maxNetWorth: 0,
    accentColor: '#94a3b8',
    roomTheme: {
      roomLayout: 'starter',
      wallColor: '#cbd5e1',
      floorColor: '#94a3b8',
      rugColor: '#64748b',
      lightingMood: 'neutral',
      maxDecorations: 0
    }
  },
  negative: {
    key: 'negative',
    name: 'Reset & Rebuild',
    badgeLabel: 'Focus Zone',
    description: 'Prioritizing debt reduction and building an emergency foundation.',
    minNetWorth: -Infinity,
    maxNetWorth: 0,
    accentColor: '#ef4444',
    roomTheme: {
      roomLayout: 'starter',
      wallColor: '#cbd5e1',
      floorColor: '#94a3b8',
      rugColor: '#64748b',
      lightingMood: 'neutral',
      maxDecorations: 2
    }
  },
  starter: {
    key: 'starter',
    name: 'Starter Haven',
    badgeLabel: 'Starter Tier',
    description: 'Building cash reserves and setting up a positive personal runway.',
    minNetWorth: 0,
    maxNetWorth: 25000,
    accentColor: '#38bdf8',
    roomTheme: {
      roomLayout: 'starter',
      wallColor: '#bae6fd',
      floorColor: '#7dd3fc',
      rugColor: '#0284c7',
      lightingMood: 'warm',
      maxDecorations: 4
    }
  },
  building: {
    key: 'building',
    name: 'Growth Studio',
    badgeLabel: 'Growth Tier',
    description: 'Accelerating asset accumulation and expanding diversified savings.',
    minNetWorth: 25000,
    maxNetWorth: 100000,
    accentColor: '#10b981',
    roomTheme: {
      roomLayout: 'expanded',
      wallColor: '#bbf7d0',
      floorColor: '#86efac',
      rugColor: '#059669',
      lightingMood: 'bright',
      maxDecorations: 6
    }
  },
  comfortable: {
    key: 'comfortable',
    name: 'Comfort Suite',
    badgeLabel: 'Comfort Tier',
    description: 'Resilient net worth with strong monthly surplus and asset stability.',
    minNetWorth: 100000,
    maxNetWorth: 300000,
    accentColor: '#6366f1',
    roomTheme: {
      roomLayout: 'suite',
      wallColor: '#c7d2fe',
      floorColor: '#a5b4fc',
      rugColor: '#4f46e5',
      lightingMood: 'sunlit',
      maxDecorations: 8
    }
  },
  established: {
    key: 'established',
    name: 'Established Estate',
    badgeLabel: 'Estate Tier',
    description: 'Long-term financial security with multi-asset growth and peace of mind.',
    minNetWorth: 300000,
    maxNetWorth: 1000000,
    accentColor: '#a855f7',
    roomTheme: {
      roomLayout: 'estate',
      wallColor: '#e9d5ff',
      floorColor: '#d8b4fe',
      rugColor: '#7e22ce',
      lightingMood: 'ambient',
      maxDecorations: 10
    }
  },
  premium: {
    key: 'premium',
    name: 'Apex Loft',
    badgeLabel: 'Apex Tier',
    description: 'Exceptional financial independence and expansive long-term capital.',
    minNetWorth: 1000000,
    maxNetWorth: Infinity,
    accentColor: '#f59e0b',
    roomTheme: {
      roomLayout: 'penthouse',
      wallColor: '#fde68a',
      floorColor: '#fcd34d',
      rugColor: '#d97706',
      lightingMood: 'golden',
      maxDecorations: 12
    }
  }
};

export const MAX_FINANCIAL_CEILING = 1e14; // 100 trillion ceiling (supports VND, IDR, JPY, KRW and large portfolios)

export function safeCleanMoneyNumber(val: any, ceiling = MAX_FINANCIAL_CEILING): number {
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  if (isNaN(num) || !isFinite(num) || num < 0) return 0;
  const clamped = Math.min(num, ceiling);
  return Math.round(clamped * 100) / 100;
}

export function cleanMoneyNumber(val: any, ceiling = 1e12): number {
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  if (isNaN(num) || !isFinite(num) || num < 0) {
    throw new Error('Financial values must be valid non-negative numbers');
  }
  if (num > ceiling) {
    throw new Error('Value exceeds allowable financial ceiling (1 trillion)');
  }
  return Math.round(num * 100) / 100;
}

export function determineWealthTier(netWorth: number, monthlySurplus = 0): WealthTierKey {
  const nw = Number(netWorth) || 0;
  if (nw < 0) return 'negative';
  if (nw < 25000) return 'starter';
  if (nw < 100000) return 'building';
  if (nw < 300000) return 'comfortable';
  if (nw < 1000000) return 'established';
  return 'premium';
}

export interface CalculatedSummary {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  monthlySurplus: number;
  visualizationTier: WealthTierKey;
  hasData: boolean;
}

export function hasEnteredFinancialData(
  assets?: Partial<FinancialAssets>,
  liabilities?: Partial<FinancialLiabilities>,
  cashFlow?: Partial<FinancialCashFlow>
): boolean {
  if (!assets && !liabilities && !cashFlow) return false;
  const a = assets || {};
  const l = liabilities || {};
  const c = cashFlow || {};
  const assetSum =
    (Number(a.cashAndSavings) || 0) +
    (Number(a.investments) || 0) +
    (Number(a.retirementAccounts) || 0) +
    (Number(a.propertyValue) || 0) +
    (Number(a.vehicles) || 0) +
    (Number(a.otherAssets) || 0);
  const liabSum =
    (Number(l.creditCardDebt) || 0) +
    (Number(l.studentLoans) || 0) +
    (Number(l.homeLoans) || 0) +
    (Number(l.otherDebts) || 0);
  const cashFlowSum =
    (Number(c.monthlyIncome) || 0) +
    (Number(c.monthlyExpenses) || 0);

  return (assetSum + liabSum + cashFlowSum) > 0;
}

export function calculateFinances(
  assets: Partial<FinancialAssets> = {},
  liabilities: Partial<FinancialLiabilities> = {},
  cashFlow: Partial<FinancialCashFlow> = {}
): {
  assets: FinancialAssets;
  liabilities: FinancialLiabilities;
  cashFlow: FinancialCashFlow;
  calculated: CalculatedSummary;
} {
  const sanitizedAssets: FinancialAssets = {
    cashAndSavings: safeCleanMoneyNumber(assets.cashAndSavings),
    investments: safeCleanMoneyNumber(assets.investments),
    retirementAccounts: safeCleanMoneyNumber(assets.retirementAccounts),
    propertyValue: safeCleanMoneyNumber(assets.propertyValue),
    vehicles: safeCleanMoneyNumber(assets.vehicles),
    otherAssets: safeCleanMoneyNumber(assets.otherAssets)
  };

  const sanitizedLiabilities: FinancialLiabilities = {
    creditCardDebt: safeCleanMoneyNumber(liabilities.creditCardDebt),
    studentLoans: safeCleanMoneyNumber(liabilities.studentLoans),
    homeLoans: safeCleanMoneyNumber(liabilities.homeLoans),
    otherDebts: safeCleanMoneyNumber(liabilities.otherDebts)
  };

  const sanitizedCashFlow: FinancialCashFlow = {
    monthlyIncome: safeCleanMoneyNumber(cashFlow.monthlyIncome),
    monthlyExpenses: safeCleanMoneyNumber(cashFlow.monthlyExpenses)
  };

  const totalAssets = Math.round(
    (sanitizedAssets.cashAndSavings +
      sanitizedAssets.investments +
      sanitizedAssets.retirementAccounts +
      sanitizedAssets.propertyValue +
      sanitizedAssets.vehicles +
      sanitizedAssets.otherAssets) * 100
  ) / 100;

  const totalLiabilities = Math.round(
    (sanitizedLiabilities.creditCardDebt +
      sanitizedLiabilities.studentLoans +
      sanitizedLiabilities.homeLoans +
      sanitizedLiabilities.otherDebts) * 100
  ) / 100;

  const netWorth = Math.round((totalAssets - totalLiabilities) * 100) / 100;
  const monthlySurplus = Math.round((sanitizedCashFlow.monthlyIncome - sanitizedCashFlow.monthlyExpenses) * 100) / 100;
  const visualizationTier = determineWealthTier(netWorth, monthlySurplus);
  const hasData = hasEnteredFinancialData(sanitizedAssets, sanitizedLiabilities, sanitizedCashFlow);

  return {
    assets: sanitizedAssets,
    liabilities: sanitizedLiabilities,
    cashFlow: sanitizedCashFlow,
    calculated: {
      totalAssets,
      totalLiabilities,
      netWorth,
      monthlySurplus,
      visualizationTier,
      hasData
    }
  };
}

export function getCurrencySymbol(currency = 'USD'): string {
  try {
    const found = ALL_CURRENCIES.find((c) => c.code === currency.toUpperCase());
    if (found) return found.symbol;
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).formatToParts(0);
    const symbolPart = parts.find((p) => p.type === 'currency');
    return symbolPart ? symbolPart.value : currency;
  } catch (e) {
    return currency;
  }
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  const safeAmount = Number(amount) || 0;
  const safeCurrency = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCurrency,
      maximumFractionDigits: 0
    }).format(safeAmount);
  } catch (e) {
    const sym = getCurrencySymbol(safeCurrency);
    return `${sym}${safeAmount.toLocaleString()}`;
  }
}
